import React from 'react';
import { logger } from '../../lib/logger';
import { notifyError } from '../../lib/errors';
import { claimChunkReload } from '../../lib/chunkReloadGuard';

type ErrorBoundaryState = { hasError: boolean; resetKey: string | undefined };

// Stale-bundle errors after a deploy: the old index references chunk hashes that no longer exist.
const CHUNK_LOAD_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

function isChunkLoadError(message: string): boolean {
  return CHUNK_LOAD_PATTERNS.some((p) => message.includes(p));
}

type ErrorBoundaryProps = React.PropsWithChildren<{
  /**
   * When this value changes while the fallback is showing, the boundary
   * clears its error and re-renders its children. Pass the route pathname
   * from a layout so navigating away from a crashed page recovers without
   * remounting the healthy subtree (keying by pathname would).
   */
  resetKey?: string;
  /**
   * `'screen'` (default) paints the fallback `min-h-screen`; `'parent'`
   * fills its parent (`h-full`) for use inside an `overflow-hidden` region
   * where a viewport-tall fallback would be clipped.
   */
  fill?: 'screen' | 'parent';
}>;

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(): Pick<ErrorBoundaryState, 'hasError'> {
    return { hasError: true };
  }

  // Clears a showing error when `resetKey` changes (e.g. the route moved on).
  // Derived-state form: setState in componentDidUpdate would paint the stale
  // fallback for one extra render.
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    return { hasError: false, resetKey: props.resetKey };
  }

  componentDidCatch(error: any, errorInfo: any) {
    const message = typeof error?.message === 'string' ? error.message : String(error);
    const pathname = window.location.pathname;
    if (isChunkLoadError(message) && claimChunkReload()) {
      logger.warn({ error: message, pathname }, '[ErrorBoundary] Chunk load failed, reloading');
      try { window.location.reload(); } catch {}
      return;
    }
    logger.error(
      {
        error: message,
        stack: typeof error?.stack === 'string' ? error.stack : undefined,
        componentStack: typeof errorInfo?.componentStack === 'string' ? errorInfo.componentStack : undefined,
        pathname,
      },
      '[ErrorBoundary] Caught error',
    );
    try { notifyError(error, 'boundary'); } catch {}
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className={`${this.props.fill === 'parent' ? 'h-full' : 'min-h-screen'} flex items-center justify-center bg-bg-secondary`}>
          <div className="rounded-xl border border-border-secondary bg-bg-raised p-6 shadow-sm text-center">
            <h1 className="mb-2 text-lg font-semibold text-content-primary">Something went wrong</h1>
            <p className="text-sm text-content-tertiary mb-4">Please try refreshing the page.</p>
            <button
              className="rounded-md bg-blueberry-500 px-4 py-2 text-sm font-semibold text-white hover:bg-blueberry-600"
              onClick={() => { try { window.location.reload(); } catch {} }}
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children as any;
  }
}


